import {AssetStatus, AssetType, Common} from '@taopix/taopix-core';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';
import {
  Icon,
  LoadingSpinnerIcon,
  MissingImageIcon,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import {PopOutProps} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import useImageLoader from '../../Hooks/Assets/useImageLoader';
import {calcRotation, flipSize} from '../../Utils/Asset';

export type MediaGalleryDetailsProps = {
  image?: AssetMetaInterface;
  used: number | undefined;
  locale: string;
  expired?: boolean;
} & Partial<Pick<PopOutProps, 'displayMode'>>;

const MediaGalleryDetails = ({
  image,
  used,
  expired = false,
  locale = 'en-GB',
  ...props
}: MediaGalleryDetailsProps) => {
  const src = useImageLoader(image, image?.assetType || AssetType.User);
  const [loaded, setLoaded] = useState(false);

  const {t} = useTranslation(['picturemanager']);

  useEffect(() => {
    setLoaded(false);
  }, [image]);

  useEffect(() => {
    setLoaded(true);
  }, [image?.rotation]);

  const formatDate = (date = '0000-00-00 00:00') => {
    const langCode = locale.startsWith('zh') ? locale.split('_').join('-') : locale;
    return new Date(date).toLocaleDateString(langCode, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const imageWrapperClasses = [
    'flex',
    'relative',
    'items-center',
    'self-center',
    'justify-center',
    'p-sm',
    'after:pt-[100%]',
    ...(props.displayMode === 'modal'
      ? [
          'w-[min(500rem,min(100svh-220rem,100vw-70px))]',
          'h-[min(500rem,min(100svh-220rem,100vw-70px))]',
        ]
      : ['w-[min(500rem,(100svh-220rem))]', 'h-[min(500rem,(100svh-220rem))]']),
  ];

  const imageClasses = ['max-w-[calc(100%-20rem)]', 'max-h-[calc(100%-20rem)]'];

  const imageDate = useMemo(() => {
    if (!image) {
      return '';
    }
    // default to the fileDateModified
    let date = image?.fileDateModified as unknown as string;
    // if we actually have the correct meta data
    if (!isNaN(Date.parse(image?.metaData?.dateTimeOriginal))) {
      date = image?.metaData?.dateTimeOriginal;
    }

    return formatDate(date);
  }, [image]);

  const rotation = useMemo(() => {
    if (!image) {
      return 0;
    }
    return calcRotation(image);
  }, [image]);

  const sizing = useMemo(() => {
    if (!image) {
      return {height: 0, width: 0};
    }
    return flipSize(rotation, {
      height: image.height,
      width: image.width,
    });
  }, [rotation, image]);

  const rotatedStyles = useMemo(() => {
    if (rotation === 0) {
      return {};
    }

    if (180 === rotation) {
      return {
        position: 'absolute',
        transform: 'rotate(180deg)',
      };
    }

    return {
      position: 'absolute',
      transform: `rotate(${rotation}deg) scale(calc(${sizing.height}/${sizing.height}))`,
      transformOrigin: 'center',
    };
  }, [sizing, rotation]) as React.CSSProperties;

  return (
    <>
      <Theme
        name={ThemeName.Container}
        className={imageWrapperClasses.join(' ')}
        allowCorners={false}
      >
        {[90, 270, 180].includes(rotation) ? (
          <canvas
            width={sizing.width}
            height={sizing.height}
            style={{width: '100%', height: '100%'}}
          ></canvas>
        ) : (
          <></>
        )}
        {image?.status === AssetStatus.Error ? (
          <Icon icon={<MissingImageIcon />} className="absolute w-xxl h-xxl" />
        ) : (
          <img
            alt={image?.name}
            src={src}
            className={imageClasses.join(' ')}
            style={loaded ? rotatedStyles : {display: 'none'}}
            onLoad={() => setLoaded(true)}
          />
        )}
        {!loaded && <Icon icon={<LoadingSpinnerIcon />} size={'large'} className={'absolute'} />}
        {expired && (
          <Theme
            name={ThemeName.Warning}
            className={'absolute bottom-[20rem] mx-[20rem] px-sm py-xs'}
          >
            {t('str_ErrorPictureHasExpired')}
          </Theme>
        )}
      </Theme>
      <div className={'h-[80rem]'}>
        <span className={'block break-words'}>
          {Common.getCurrentLocaleString(image?.name || '', true)}
        </span>
        <span className={'block truncate'}>{imageDate}</span>
        {undefined !== used && (
          <span className={'block truncate'}>
            {t('str_LabelUsed', {ns: 'picturemanager'}) + used}
          </span>
        )}
      </div>
    </>
  );
};

export default MediaGalleryDetails;
